import { forwardRef, useEffect, useRef, useState } from 'react'
import * as m from 'motion/react-m'

import type { ButtonProps } from '@buffer-mono/popcorn'
import {
  Button,
  MessageCircleHeartIcon,
  Tooltip,
  useLocalStorage,
} from '@buffer-mono/popcorn'

import styles from '../FeedbackWidget.module.css'

const STORAGE_KEY_PREFIX = 'feedback-widget-seen'

/**
 * The space between the icon and the label is animated along with the width
 * rather than set as a `gap` on the button, so that it collapses to nothing
 * with the text. A static gap would leave the icon sitting off-centre while
 * the trigger is collapsed.
 */
const EXPANDED_TEXT = { maxWidth: 200, paddingLeft: 4 }
const COLLAPSED_TEXT = { maxWidth: 0, paddingLeft: 0 }
const TRIGGER_LABEL = 'Share Feedback'

/**
 * How long the label takes to collapse. The tooltip suppression below is timed
 * off the same number, so the two cannot drift apart.
 */
export const COLLAPSE_DURATION_MS = 800

interface DefaultTriggerProps extends Omit<ButtonProps, 'id' | 'children'> {
  id: string
  isOpen: boolean
  hasData: boolean
}

const DefaultTrigger = forwardRef<HTMLButtonElement, DefaultTriggerProps>(
  function DefaultTrigger({ id, isOpen, hasData, className, ...props }, ref) {
    const storageKey = `${STORAGE_KEY_PREFIX}-${id}`
    const [hasBeenSeen, setHasBeenSeen] = useLocalStorage(storageKey, false)

    // Determine if we should show collapse animation on mount
    const [showCollapseAnimation] = useState(() => {
      if (!hasBeenSeen) {
        return true
      }

      // 5% chance to show collapse animation on subsequent views
      return Math.random() < 0.05
    })

    // Mark as seen after first render
    useEffect(() => {
      if (!hasBeenSeen) {
        setHasBeenSeen(true)
      }
    }, [hasBeenSeen, setHasBeenSeen])

    // Expand when popover is open or when form has data
    const shouldExpand = isOpen || hasData

    // The label needs the full transition to collapse, so it is still on screen
    // after the popover closes. Handing the tooltip back to Radix while it is
    // there puts the same words on screen twice, because the pointer is still
    // over the trigger that was just clicked.
    // On a first view the label starts expanded and collapses, so it is on
    // screen from the first frame — not only after the popover closes.
    const [isCollapsing, setIsCollapsing] = useState(
      showCollapseAnimation && !shouldExpand,
    )
    const wasExpanded = useRef(shouldExpand)

    useEffect(() => {
      if (wasExpanded.current && !shouldExpand) {
        setIsCollapsing(true)
      }
      wasExpanded.current = shouldExpand
    }, [shouldExpand])

    // Ends the suppression however the collapse started.
    useEffect(() => {
      if (!isCollapsing) {
        return
      }

      const timer = setTimeout(
        () => setIsCollapsing(false),
        COLLAPSE_DURATION_MS,
      )

      return (): void => {
        clearTimeout(timer)
      }
    }, [isCollapsing])

    const isLabelVisible = shouldExpand || isCollapsing

    return (
      <Tooltip
        content="Share feedback"
        open={isLabelVisible ? false : undefined}
      >
        <Button
          ref={ref}
          variant="tertiary"
          size="medium"
          className={`${styles.triggerButton} ${className ?? ''}`}
          // Lets the command bar's "Send Feedback" action find and click the
          // widget instead of falling through to a mailto: link.
          data-feedback-widget-trigger
          {...props}
          aria-label={TRIGGER_LABEL}
        >
          <MessageCircleHeartIcon />

          <m.span
            initial={showCollapseAnimation ? EXPANDED_TEXT : COLLAPSED_TEXT}
            animate={shouldExpand ? EXPANDED_TEXT : COLLAPSED_TEXT}
            transition={{
              duration: COLLAPSE_DURATION_MS / 1000,
              ease: 'easeInOut',
            }}
            className={styles.triggerText}
          >
            {TRIGGER_LABEL}
          </m.span>
        </Button>
      </Tooltip>
    )
  },
)

export { DefaultTrigger }
